<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import type { World } from '../../game/World';
	import { WebGLRenderer, PerspectiveCamera, Raycaster, Vector2, sRGBEncoding } from 'three';
	import type { Camera, Intersection } from 'three';
	import { currentLevel } from '../../stores/current-level';
	import { adjacentFromOppositeAngle, deg2rad } from '../../utils/trigonometry';
	import { LevelObject } from '../../game/LevelObject';

	export let world: World;

	const id = 'world-view-' + Date.now();

	const renderer = new WebGLRenderer();
	renderer.outputEncoding = sRGBEncoding;

	const raycaster = new Raycaster();

	let camera: Camera;
	let div: HTMLDivElement;

	let hoverObject: LevelObject | null = null;
	let hoverTile: Vector2 | null = null;

	onMount( () => {
		window.addEventListener( 'resize', onResize );
		onResize();
		div.appendChild( renderer.domElement );

		const animate = () => {
			requestAnimationFrame( animate );
			renderer.render( world.getScene(), camera );
		};
		animate();
	} );

	onDestroy( () => {
		window.removeEventListener( 'resize', onResize );
	} );

	// Track the size of the current level.
	let levelWidth: number = 1;
	let levelHeight: number = 1;
	$: {
		levelWidth = $currentLevel.width;
		levelHeight = $currentLevel.height;

		onResize();
	}

	function onResize() {
		if ( ! div ) {
			return;
		}

		renderer.setSize( div.clientWidth, div.clientHeight );

		const verticalFov = 60;
		const fovRatio = div.clientWidth / div.clientHeight;
		const horizontalFov = fovRatio * verticalFov;

		// Figure out how high to place the camera to fit the board as nicely as possible.
		const horzFitHeight = Math.abs( adjacentFromOppositeAngle( levelWidth / 2 + 0.7, deg2rad( horizontalFov / 2 ) ) );
		const vertFitHeight = Math.abs( adjacentFromOppositeAngle( levelHeight / 2 + 0.7, deg2rad( verticalFov / 2 ) ) );

		const fitHeight = Math.max( horzFitHeight, vertFitHeight );

		camera = new PerspectiveCamera( verticalFov, fovRatio, 0.1, 1000 );
		camera.position.y = fitHeight;
		camera.position.z = 1.0;
		camera.lookAt( 0, 0, 0 );
	}

	function getFirstIntersection( pos: Vector2 ): [ LevelObject | null, Intersection | null ] {
		if ( ! camera ) {
			return [ null, null ];
		}

		raycaster.setFromCamera( pos, camera );
		const hits = raycaster.intersectObjects( world.getScene().children, true );
		for ( const hit of hits ) {
			const obj = LevelObject.findInObject3D( hit.object );
			if ( obj ) {
				return [ obj, hit ];
			}
		}

		return [ null, null ];
	}

	function onHover( mousePos: Vector2 ) {
		const [ object, intersection ] = getFirstIntersection( mousePos );
		hoverObject = object;

		if ( ! intersection ) {
			hoverTile = null;
			return;
		}

		// Clear hovertile if out of bounds.
		const tile = world.levelMap.worldToTile( intersection.point );
		const inBounds = world.levelMap.inBounds( tile );
		if ( ! inBounds ) {
			hoverTile = null;
		}

		// Only update hovertile if different.
		if ( hoverTile === null || hoverTile.x !== tile.x || hoverTile.y !== tile.y ) {
			hoverTile = tile;
		}
	}

	function onMouseMove( event: MouseEvent ) {
		const rect = div.getBoundingClientRect();
		onHover( new Vector2(
			( event.x - rect.left ) / rect.width * 2 - 1,
			- ( event.y - rect.top ) / rect.height * 2 + 1
		) );
	}
</script>

<div class="world-view" {id} on:mousemove={ onMouseMove } bind:this={ div }>
	{#if hoverTile || hoverObject}
		<div class="info">
			{#if hoverTile}
				[ {hoverTile.x}, {hoverTile.y} ]
				<br/>
			{/if}
			{#if hoverObject && hoverObject.type}
				{ hoverObject.type }
				<br/>
				{#each Object.entries( hoverObject.getProperties() ) as [ key, value ] }
					<b>{ key }:</b> { value }
					<br/>
				{/each}
			{/if}
		</div>
	{/if}
</div>

<style>
	.world-view {
		width: 100%; height: 100%;
	}

	.info {
		position: absolute;
		top: 10px;
		left: 10px;
		color: white;
	}
</style>
